import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, ArrowLeft, ShoppingCart, X, MapPin } from 'lucide-react';
import axios from 'axios';
import ProductRow from '@/components/ProductRow';
import { useStore } from '@/contexts/StoreContext';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';

const API = window.location.origin + '/api';

export default function CategoryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { selectedStore, selectedBranch } = useStore();
  const { user } = useAuth();
  const { cartItems, cartCount, getCartTotal, fetchCart, clearCart } = useCart();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [wishlistIds, setWishlistIds] = useState([]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (slug === 'discounts') params.discount = true;
      else params.category = slug;
      if (search) params.search = search;
      if (selectedStore) params.supermarket = selectedStore.slug;
      const { data } = await axios.get(`${API}/products`, { params });
      setProducts(data);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
    setLoading(false);
  }, [slug, search, selectedStore]);

  useEffect(() => {
    axios.get(`${API}/categories/${slug}`)
      .then(r => setCategory(r.data))
      .catch(err => console.error('Failed to load category:', err));
  }, [slug]);

  useEffect(() => {
    const timer = setTimeout(fetchProducts, 300);
    return () => clearTimeout(timer);
  }, [fetchProducts]);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  useEffect(() => {
    if (!user || user === false) { setWishlistIds([]); return; }
    axios.get(`${API}/wishlist/ids`, { withCredentials: true })
      .then(r => setWishlistIds(r.data || []))
      .catch(err => console.error('Failed to fetch wishlist ids:', err.message));
  }, [user]);

  const handleWishlistToggle = useCallback(async (productId) => {
    if (!user || user === false) { navigate('/login'); return; }
    try {
      const { data } = await axios.post(`${API}/wishlist/${productId}`, {}, { withCredentials: true });
      setWishlistIds(prev => data.wishlisted ? [...prev, productId] : prev.filter(id => id !== productId));
    } catch (err) {
      console.error('Failed to toggle wishlist:', err.message);
    }
  }, [user, navigate]);

  const cartTotal = useMemo(() => getCartTotal(), [getCartTotal]);

  const handleClear = useCallback(async () => {
    if (!window.confirm('Clear all items from your cart?')) return;
    await clearCart();
  }, [clearCart]);

  return (
    <div className="category-page" data-testid="category-page">
      <button className="back-btn" onClick={() => navigate(selectedStore ? `/store/${selectedStore.slug}` : '/')} data-testid="back-btn">
        <ArrowLeft /> Back
      </button>
      <h1 className="category-title" data-testid="category-title">{category?.name || slug}</h1>
      {selectedStore && (
        <p className="category-subtitle" style={{ textAlign: 'center' }}>
          Shopping at <strong style={{ color: 'var(--navy)' }}>{selectedStore.name}</strong>
          {selectedBranch ? (
            <>
              {' '}—{' '}
              <button
                type="button"
                className="branch-pill"
                onClick={() => navigate(`/store/${selectedStore.slug}`)}
                data-testid="branch-pill"
                title="Change branch"
              >
                <MapPin size={11} /> {selectedBranch.name}
              </button>
            </>
          ) : (
            <>
              {' '}—{' '}
              <button
                type="button"
                className="branch-pill branch-pill-warn"
                onClick={() => navigate(`/store/${selectedStore.slug}`)}
                data-testid="branch-pill-missing"
              >
                <MapPin size={11} /> Choose a branch
              </button>
            </>
          )}
        </p>
      )}

      <div className="search-bar" data-testid="search-bar">
        <Search size={18} />
        <input
          type="text"
          placeholder="Search Products..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          data-testid="search-input"
        />
      </div>

      {cartCount > 0 && (
        <div className="category-selection-bar" data-testid="selection-bar">
          <div className="category-selection-bar-info">
            <span className="category-selection-bar-count" data-testid="selection-bar-count">
              {cartCount} item{cartCount !== 1 ? 's' : ''} in cart
            </span>
            <span className="category-selection-bar-total" data-testid="selection-bar-total">
              Running total: {cartTotal.toFixed(2)} PGK
            </span>
          </div>
          <div className="category-selection-bar-actions">
            <button
              type="button"
              className="category-selection-bar-btn category-selection-bar-btn-ghost"
              onClick={handleClear}
              data-testid="selection-bar-clear"
              aria-label="Clear cart"
            >
              <X size={14} /> Clear
            </button>
            <button
              type="button"
              className="category-selection-bar-btn"
              onClick={() => navigate('/cart')}
              data-testid="selection-bar-checkout"
            >
              <ShoppingCart size={14} /> Review Cart
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-screen">Loading products...</div>
      ) : products.length === 0 ? (
        <div className="empty-state">
          <p>No products found{search ? ` for "${search}"` : ''}{selectedStore ? ` at ${selectedStore.name}` : ''}</p>
        </div>
      ) : (
        <div className="product-row-list" data-testid="product-row-list">
          {products.map(product => (
            <ProductRow
              key={product.id}
              product={product}
              wishlisted={wishlistIds.includes(product.id)}
              onWishlistToggle={handleWishlistToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}
